export const DOCS_CONTEXT = `
FrankieOne V1 API — Reference Data for Documentation:
Public Testbed Spreadsheet: https://docs.google.com/spreadsheets/d/1QDrjffj9VyL_ZthHNIMHJvrwAgQphKS_/edit?gid=64890954#gid=64890954

====== OFFICIAL KYC TESTBED PERSONAS (UAT) ======
| Name                        | Expected Result           | Doc Type(s)                | Country |
|-----------------------------|---------------------------|----------------------------|---------|
| JAMES A TESTONE             | CLEAR (PASS)              | Driver Licence + Medicare  | AUS     |
| JUDY TESTTWO                | CLEAR (PASS)              | Driver Licence             | AUS     |
| PETER TESTTHREE             | CLEAR (PASS)              | Driver Licence             | AUS     |
| MARY TESTFOUR               | CLEAR (PASS)              | Driver Licence             | AUS     |
| GEOFF TESTSEVEN             | CLEAR (PASS)              | Medicare                   | AUS     |
| JENNY TESTEIGHT             | FAIL (KYC Not Found)      | Driver Licence             | AUS     |
| JAMES G TESTELEVEN          | PEP match                 | Passport                   | AUS     |
| BRUCE TESTNINETEEN          | SANCTION match            | -                          | AUS     |
| STACY K TESTTWENTY          | PEP + SANCTION            | -                          | AUS     |
| DIANE NICHOLSON             | PEP + SANCTIONS flags     | see testbed spreadsheet    | AUS     |
| Doug J Jeffries             | CLEAR (PASS)              | SSN (NATIONAL_ID)          | USA     |
| RONALD Testonehundredfour   | CLEAR (PASS)              | Aadhaar (NATIONAL_ID)      | IND     |
| THOMAS Testninetythree      | CLEAR (PASS)              | National ID                | CHN     |

RECOMMENDED: Clean KYC PASS = JAMES A TESTONE | PEP+Sanctions = DIANE NICHOLSON

====== OFFICIAL KYB TESTBED ENTITIES (AUS) ======
SIMPLE:  FOX PTY. LTD. | ACN: 052121347 | ABN: 37052121347 - 2 entities, all PASS, no AML flags
MEDIUM:  BCP (PITT STREET) PTY LTD | ACN: 650193865
COMPLEX: CLARO DISABILITY SERVICES PTY LTD | ACN: 082554630 - up to 5min, large structure

====== PORTAL URLs (V1) ======
UAT Portal:        https://portal.kycaml.uat.frankiefinancial.io/login
Production Portal: https://portal.kycaml.frankiefinancial.io/login
Portal access: Contact presales team.

====== BASE URLs (V1) ======
UAT API:  https://api.kycaml.uat.frankiefinancial.io/compliance/v1.2/
Prod API: https://api.kycaml.frankiefinancial.io/compliance/v1.2/

====== AUTH HEADERS (V1) - CASE SENSITIVE ======
api_key                   REQUIRED  All lowercase with underscore
X-Frankie-CustomerID      REQUIRED  Uppercase ID at end - NOT CustomerId
X-Frankie-CustomerChildID OPTIONAL  Separate api_key required per child
X-Frankie-Channel         OPTIONAL  api / portal / smartui / custom
X-Frankie-Username        OPTIONAL  Operator email - recommended for audit

====== CREATE ENTITY ENDPOINT ======
POST /compliance/v1.2/entity
Returns: 200 OK + entityId - entity appears in UAT portal immediately.

====== SPECIAL TEST SCENARIOS ======
middleName = "FailNonID"           forces ID checks to pass but all others fail
streetName = "77 Equifax Street"   simulates Equifax failure
streetName = "123 Experian Street" simulates Experian failure
PEP/Sanctions matches are STATIC and pre-configured in UAT.
`

export interface Section {
  id: string
  title: string
  emoji: string
  hint: string
  apiOnly?: boolean
}

export const SECTIONS: Section[] = [
  { id: "overview",     title: "Overview & Getting Started",        emoji: "\u{1F680}", hint: "What is FrankieOne, environments, UAT vs Production, first API call" },
  { id: "auth",         title: "Authentication & Headers",          emoji: "\u{1F511}", hint: "Header format, case-sensitivity, code examples, error fixes", apiOnly: true },
  { id: "portal",       title: "Portal Access & Navigation",        emoji: "\u{1F5A5}\uFE0F", hint: "Requesting access, portal URLs, team management" },
  { id: "resources",    title: "Resources & Support",               emoji: "\u{1F4DA}", hint: "V1 docs, AI assistant, Teams/Slack support channels" },
  { id: "int_diagrams", title: "Integration Diagrams",              emoji: "\u{1F517}", hint: "Architecture overview, system integration patterns" },
  { id: "recipes",      title: "Recipes, Rulesets & Cascade",       emoji: "\u2699\uFE0F", hint: "Rulesets, cascade, configured recipes, check types" },
  { id: "kyc",          title: "Verifying Individuals (KYC)",       emoji: "\u{1F50D}", hint: "Create entity, run verification, interpret results, test with JAMES A TESTONE" },
  { id: "idv",          title: "Identity Document Verification (IDV)", emoji: "\u{1FAAA}", hint: "OneSDK, onboarding URL, document capture, OCR, test with sample ID" },
  { id: "kyb",          title: "Verifying Businesses (KYB)",        emoji: "\u{1F3E2}", hint: "ABR lookup, UBO ownership, run KYC per UBO, test with FOX PTY LTD" },
  { id: "aml",          title: "AML, PEP & Sanctions Screening",   emoji: "\u{1F6E1}\uFE0F", hint: "Screening, resolution, monitoring, test with DIANE NICHOLSON" },
  { id: "risk",         title: "Risk Attributes & KVPs",          emoji: "\u{1F4CB}", hint: "Custom risk attributes via extraData key-value pairs, best practices" },
  { id: "sso",          title: "SSO (SAML) Setup",                emoji: "\u{1F510}", hint: "SAML 2.0 SSO for portal, Okta/Azure config, attribute mapping" },
  { id: "webhooks",     title: "Webhooks & Notifications",          emoji: "\u{1F514}", hint: "Webhook setup, notification types, retrieve endpoint, retry logic", apiOnly: true },
  { id: "diagrams",     title: "Sequence Diagrams",                 emoji: "\u{1F4CA}", hint: "KYC, KYB, IDV, and AML call flows", apiOnly: true },
]

export const STATUS_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  approved:   { color: "#4ecca3", icon: "\u2713", label: "Approved" },
  ready:      { color: "#f0c040", icon: "\u25CF", label: "Review" },
  generating: { color: "#74b9ff", icon: "\u27F3", label: "Working..." },
  error:      { color: "#ff6b6b", icon: "\u2717", label: "Error" },
  undefined:  { color: "#555",    icon: "\u25CB", label: "Pending" },
}
